import { forwardRef, type ReactNode, type ButtonHTMLAttributes } from 'react'
import { Link } from 'react-router-dom'

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonBaseProps {
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: ReactNode
  iconPosition?: 'left' | 'right'
  children?: ReactNode
  className?: string
}

interface ButtonAsButton
  extends ButtonBaseProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof ButtonBaseProps> {
  as?: 'button'
  href?: never
  to?: never
  external?: never
}

interface ButtonAsLink extends ButtonBaseProps {
  as: 'link'
  to: string
  href?: never
  external?: never
  onClick?: () => void
}

interface ButtonAsAnchor
  extends
    ButtonBaseProps,
    Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof ButtonBaseProps | 'href'> {
  as: 'a'
  href: string
  external?: boolean
  to?: never
}

type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAnchor

const variantStyles: Record<ButtonVariant, string> = {
  primary: 'bg-gray-800 text-white hover:bg-gray-700 focus:ring-gray-800',
  secondary: 'bg-white/50 border border-black/20 text-black hover:bg-black/5 focus:ring-black',
  ghost: 'text-black/60 hover:text-black hover:bg-black/5 focus:ring-black',
  danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
}

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-sm gap-1.5',
  md: 'px-4 py-2 text-sm gap-2',
  lg: 'px-6 py-3 text-base gap-2',
}

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>((props, ref) => {
  const {
    variant = 'primary',
    size = 'md',
    icon,
    iconPosition = 'left',
    children,
    className = '',
    ...rest
  } = props

  const baseStyles =
    'inline-flex items-center justify-center font-medium rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed'
  const styles = `${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`

  const content = (
    <>
      {icon && iconPosition === 'left' && <span className="flex-shrink-0">{icon}</span>}
      {children && <span>{children}</span>}
      {icon && iconPosition === 'right' && <span className="flex-shrink-0">{icon}</span>}
    </>
  )

  if (props.as === 'link') {
    return (
      <Link
        to={props.to}
        className={styles}
        ref={ref as React.Ref<HTMLAnchorElement>}
        onClick={props.onClick}
      >
        {content}
      </Link>
    )
  }

  if (props.as === 'a') {
    const {
      as: _as,
      href,
      external,
      variant: _v,
      size: _s,
      icon: _i,
      iconPosition: _ip,
      ...anchorProps
    } = props
    return (
      <a
        href={href}
        className={styles}
        ref={ref as React.Ref<HTMLAnchorElement>}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        {...anchorProps}
      >
        {content}
      </a>
    )
  }

  const { as: _asButton, ...buttonProps } = rest as ButtonAsButton
  return (
    <button className={styles} ref={ref as React.Ref<HTMLButtonElement>} {...buttonProps}>
      {content}
    </button>
  )
})

Button.displayName = 'Button'

export default Button
export type { ButtonProps, ButtonVariant, ButtonSize }
